/**
 * @fileoverview useAnimationFrameCallback — a component-owned animation frame.
 *
 * Internal to `@loci/ui` (WEB-1108). `apps/web` keeps its own copy in
 * `hooks/useAnimationFrameCallback.ts` so that web tests that `vi.mock('@loci/ui')`
 * do not have to stub it.
 */
import { useCallback, useEffect, useRef } from 'react';
/**
 * Returns a stable `schedule(callback)` that runs `callback` on the next
 * animation frame.
 *
 * Scheduling again replaces a frame that has not run yet, and unmounting
 * cancels it, so the callback never runs after the component that asked for
 * it is gone. Use it instead of a bare `requestAnimationFrame` in an event
 * handler, which has no cleanup.
 */
export function useAnimationFrameCallback() {
    const frameRef = useRef(0);
    useEffect(() => () => {
        if (frameRef.current !== 0)
            window.cancelAnimationFrame(frameRef.current);
        frameRef.current = 0;
    }, []);
    return useCallback((callback) => {
        if (typeof window === 'undefined')
            return;
        if (frameRef.current !== 0)
            window.cancelAnimationFrame(frameRef.current);
        frameRef.current = window.requestAnimationFrame((time) => {
            frameRef.current = 0;
            callback(time);
        });
    }, []);
}
